import React from 'react'
import { Stack, Text, Group, Button, Skeleton } from 'components'
import { IconCheck } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { Topic } from '../../../types'

interface TopicSelectionSectionProps {
  topics: Topic[]
  selectedTopics: string[]
  onToggle: (topicValue: string) => void
  loading?: boolean
  error?: boolean
}

export const TopicSelectionSection: React.FC<TopicSelectionSectionProps> = ({
  topics,
  selectedTopics,
  onToggle,
  loading,
  error
}) => {
  const { t } = useTranslation()

  return (
    <Stack spacing="md">
      <div>
        <Group spacing="xs" mb="0">
          <Text size="lg" weight={500}>
            {t('arc_main_topics_question')}
            <Text color="red" display="inline"> *</Text>
          </Text>
        </Group>
        <Text size="lg" color="dimmed" mb="xs">
          {t('arc_select_subject_areas')}
        </Text>

        {loading ? (
          <Group spacing="sm">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} height={32} width={100} radius="xl" />
            ))}
          </Group>
        ) : (
          <Group spacing="sm">
            {topics.map(topic => {
              const isSelected = selectedTopics.includes(topic.value)
              return (
                <Button
                  key={topic.value}
                  onClick={() => onToggle(topic.value)}
                  variant={isSelected ? 'filled' : 'outline'}
                  color="teal"
                  size="lg"
                  radius="xl"
                  rightIcon={isSelected ? <IconCheck size={14} /> : null}
                  styles={(theme) => ({
                    root: {
                      backgroundColor: isSelected
                        ? theme.colors.teal[6]
                        : theme.colors.teal[0],
                      borderColor: theme.colors.teal[6],
                      color: isSelected ? 'white' : theme.colors.teal[7],
                      fontWeight: isSelected ? 500 : 400,
                      '&:hover': {
                        backgroundColor: isSelected
                          ? theme.colors.teal[7]
                          : theme.colors.teal[1],
                      },
                      padding: '4px 12px',
                      height: 'auto',
                    },
                    rightIcon: {
                      marginLeft: 4,
                      color: 'white',
                    }
                  })}
                >
                  {topic.label}
                </Button>
              )
            })}
          </Group>
        )}

        {error && (
          <Text size="sm" color="red" mt={8}>
            {t('arc_field_required')}
          </Text>
        )}
      </div>
    </Stack>
  )
}